import { Component, ViewChild, ViewChildren, QueryList, ElementRef, ViewEncapsulation, AfterViewInit, Inject, Optional, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ButtonTriggerEvent } from 'src/app/common/components/button/button.component';
import { ImageLinkFileSelectorComponent } from 'src/app/common/components/image.link.file.selector/image.link.file.selector.component';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { IAppState } from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import {
  EditPlaceCategoryModalActions,
  EditPlaceCategoryModalComponent,
  IEditPlaceCategoryModalData,
} from '../edit.place.category.modal/edit.place.category.modal.component';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { catchError, finalize, of, switchMap, takeUntil, tap, timer } from 'rxjs';
import { CreatePlaceCommand } from 'src/app/modules/shared/models/create.place.command';
import { VisitorEventService } from 'src/app/modules/event-calendar/services/visitor.event.service';
import { OpeningHours } from 'src/app/modules/shared/models/visitor.place';
import { HoursInputComponent } from 'src/app/common/components/hours.input/hours.input.component';
import { WixChangeDetectionHelper } from 'src/app/common/helpers/wix-change-detection.helper';
import { AddressInputComponent } from 'src/app/common/components/address.input/address.input.component';

@Component({
  selector: 'ig-create-place',
  templateUrl: './create.place.component.html',
  styleUrls: ['./create.place.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class CreatePlaceComponent extends BaseComponent implements AfterViewInit {
  form: FormGroup;
  categoryText = '';
  isCssReady = false;
  hasCopyrights = false;
  hasSubmittedSuccessfully = false;
  imageUploaders: number = 5;
  imgUploadType: 'upload' | 'link' | 'unset' = 'unset';
  readonly submitSuccessMessage =
    'Thank you for submitting your place! Please allow at least 5 business days for review and approval.';

  uploadedImages: string[] = [];
  imageLinks: string = '';
  @ViewChild(AddressInputComponent, { static: false })
  addressInput: AddressInputComponent;
  @ViewChildren('submitSuccessBanner')
  submitSuccessBanners: QueryList<ElementRef<HTMLElement>>;
  hint: string =
    'When entering multiple image URLs, please separate them with a semicolon. You can enter up to a maximum of 5.';

  constructor(
    public activatedRoute: ActivatedRoute,
    public placeService: VisitorPlaceService,
    @Optional() public dialogRef: MatDialogRef<CreatePlaceComponent>,
    public modalService: ModalService,
    private _fb: FormBuilder,
    private _errorAlertService: ErrorAlertService,
    @Inject(MAT_DIALOG_DATA) @Optional() public data: ICreatePlaceModalData,
    private _store: Store<IAppState>,
    public cdRef: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {
    super({ activatedRoute });

    data = data || { isForHub: false };

    this.resetForm();

    // handle browser back button click, close the event details view and reload the page
    window.addEventListener('popstate', this.hashChanged.bind(this));

    // as this modal is ShadowDom, we need to inject the styles into it
    dialogRef?.afterOpened().subscribe(() => {
      modalService.injectStyle(
        '.cdk-overlay-container ig-create-place',
        [],
        () => { this.isCssReady = true; },
        this.cdRef,
        'CreatePlaceComponent',
      );
    });

    if (!dialogRef) {
      setTimeout(() => {
        modalService.injectStyle(
          'ig-create-place',
          [],
          () => { this.isCssReady = true; },
          this.cdRef,
          'CreatePlaceComponent-Embedded',
        );
      }, 200);
    }
  }

  ngAfterViewInit(): void {
    this.checkUsingImgLink();
  }

  public get isEmbedded() {
    return !this.dialogRef;
  }

  public get isFormInValid() {
    return this.form.invalid;
  }

  public get isUsingLink() {
    return !!this.form.get('imageLink').value;
  }

  public onImageUploaded(url: string) {
    console.log('Url:', url);
    if (this.isUsingLink) {
      this.form.get('imageLink').setValue(null);
    }

    if (this.uploadedImages.indexOf(url) === -1) {
      this.uploadedImages.push(url);
    }
  }

  public onRemoveImage(url: string) {
    if (this.uploadedImages.indexOf(url) > -1) {
      this.uploadedImages = this.uploadedImages.filter((x) => x !== url);
    }
  }

  setAsApprovedChange($event) {
    this.form.get('isApproved').setValue($event.checked);
  }

  private addressValidator(control: FormControl) {
    const value = control.value;

    if (!value) {
      return { required: true };
    }

    if (typeof value === 'string') {
      return { invalidAddress: true };
    }

    if (
      typeof value === 'object' &&
      (!value.address || (!value.address_components && !value.addressComponents))
    ) {
      return { invalidAddress: true };
    }

    return null;
  }

  private processAddress() {
    const address = this.form.get('address').value;
    if (address?.address_components) {
      address.addressComponents = address.address_components;
      delete address.address_components;
    }
    return address;
  }

  submit(event: ButtonTriggerEvent) {
    if (this.hasSubmittedSuccessfully && !this.data?.isForHub) {
      event.immediateComplete();
      return;
    }

    this.form.markAllAsTouched();

    const addressControl = this.form.get('address');

    if (this.addressInput?.hasTextButNoSelection()) {
      addressControl.setErrors({ invalidAddress: true });
      addressControl.updateValueAndValidity();
    } else {
      addressControl.updateValueAndValidity();
    }

    const address = this.form.get('address').value;
    if (typeof address === 'string') {
      this._errorAlertService.showErrorAlert(
        'Please type the street address and select the correct address from the drop-down.'
      );
      event.immediateComplete();
      return;
    }

    if (addressControl.invalid) {
      this._errorAlertService.showErrorAlert(
        'The Location/Address field is invalid. Please type the street address and select the correct address from the drop-down.'
      );
      event.immediateComplete();
      return;
    }

    const imageLink = this.form.get('imageLink').value;
    if (imageLink && imageLink.split(';').length > 5) {
      this._errorAlertService.showErrorAlert('You can only enter up to 5 image URLs.');
      event.immediateComplete();
      return;
    }

    if (this.isUsingLink || (this.uploadedImages && this.uploadedImages.length > 0)) {
      if (!this.hasCopyrights) {
        this._errorAlertService.showErrorAlert(
          'Please confirm that you have the rights to use the images you are uploading.'
        );
        event.immediateComplete();
        return;
      }
    }

    if (this.form.invalid) {
      event.immediateComplete();
      return;
    }

    if (this.data?.isForHub) {
      this.form.get('isHubCreated').setValue(true);
    }
    let command = <CreatePlaceCommand>this.form.value;
    command.address = this.processAddress();
    command.imageLink = this.uploadedImages.join(';');
    const createdCommand = <CreatePlaceCommand>{ ...command };
    
    // opening_hours is already an OpeningHours object from the HoursInputComponent
    command.opening_hours = this.form.get('opening_hours').value;
    createdCommand.opening_hours = command.opening_hours;

    this.placeService.createPlace(this.hubName, command).subscribe({
      next: (res) => {
        event.success();
        VisitorEventService.itemCreatedSubject$.next(createdCommand);
        if (!this.data?.isForHub) {
          this.hasSubmittedSuccessfully = true;
          this.scrollToSuccessState();
          return;
        }
        this.resetForm();
        if (this.data?.isForHub) {
          setTimeout(() => this.close(), 3000);
        }
      },
      error: (err) => {
        const message = err?.error?.error || err?.error?.message || err?.message || 'Failed to save place. Please try again.';
        this._errorAlertService.showErrorAlert(message);
        event.immediateComplete();
      },
    });
  }

  submitAnotherListing() {
    this.hasSubmittedSuccessfully = false;
    this.resetForm();
    setTimeout(() => {
      this.addressInput?.clear();
    }, 0);
    this.scrollFormToTop();
  }

  close() {
    this.dialogRef?.close();
  }

  showEditCategoryModal() {
    // WIX FIX: Force detection before opening modal
    WixChangeDetectionHelper.forceChangeDetection(this.cdRef, 'Before opening category modal');
    
    let actions = <EditPlaceCategoryModalActions>{
      confirm: (event: ButtonTriggerEvent, category) => {
        // this.categoryText = category.categoryText;
        // const scategory = category.category;

        this.form.get('category').setValue(category.categoryText);
        
        // WIX FIX: Force detection after category selection
        WixChangeDetectionHelper.forceChangeDetection(this.cdRef, 'After category selection');
      },
    };

    this.modalService.show({
      component: EditPlaceCategoryModalComponent,
      // providers: [
      //   { provide: EditPlaceCategoryModalActions, useValue: actions },
      //   { provide: VisitorPlace, useValue: null },
      // ],
      panelClass: 'ig-modal-w-70p',
      data: {
        actions,
        place: {
          categoryList: this.form.get('category').value
            ? this.form
                .get('category')
                .value.split(',')
                .map((x) => this.toCode(x.trim()))
            : [],
        } as VisitorPlace,
      } as IEditPlaceCategoryModalData,
    });
  }

  toCode(category: string) {
    return this.placeService.toCategoryCode(category);
  }

  resetForm() {
    this.form = this._fb.group({
      name: [null, [Validators.required]],
      description: [null, []],
      address: [null, [Validators.required, this.addressValidator.bind(this)]],
      website: [null, []],
      facebookLink: [null, []],
      category: [null, [Validators.required]],
      phone: [null, []],
      imageLink: [null, []],
      imgFile: [null, []],
      ownerEmail: [null, []],
      ownerPhone: [null, []],
      hasCopyrights: [false, []],
      isApproved: [false, []],
      isHubCreated: [false, []],
      opening_hours: [null, []],
    });
    this.uploadedImages = [];
    this.imageLinks = '';
    this.hasCopyrights = false;
  }

  /**
   * handle browser back button click, close the view
   * only close the page is not enough, it will always have other loading issues for the script embedding solution
   * so force reload the page for now
   * */
  // @HostListener('window:popstate', ['$event'])
  private hashChanged($event: Event) {
    $event.preventDefault();
    let url = window.location.href.split('imgoing-place')[0];
    url = url.endsWith('?') ? url.substring(0, url.length - 1) : url;
    window.location.href = url;
    this.close();
  }

  private checkUsingImgLink() {
    this.form
      .get('imageLink')
      .valueChanges.pipe(
        tap((value) => {
          this.imageLinks = value;
        })
      )
      .subscribe();
  }

  private scrollToSuccessState() {
    setTimeout(() => {
      const banner = this.submitSuccessBanners?.last?.nativeElement;
      if (!banner) {
        this.scrollFormToTop();
        return;
      }
      banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  private scrollFormToTop() {
    const hostElement = this.elementRef?.nativeElement as HTMLElement;
    const scrollSection = hostElement?.querySelector('.form-scroll-section') as HTMLElement;
    scrollSection?.scrollTo({ top: 0, behavior: 'smooth' });
    hostElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

}

export interface ICreatePlaceModalData {
  isForHub: boolean;
}
